import { debugLog } from "../Utils/LogUtils";
import {
    generateEnrolmentDocumentPDFToBlob
} from "./EnrollmentPackageGeneration";
import React, {useEffect, useState} from "react";
import {
    enrolmentValidation, PLAN_TYPE_IDS, stateWithPlan2AsPrimary
} from "../Utils/PlanUtils";
import EnrollmentProgressStepper from "../EnrollmentProgressStepper/EnrollmentProgressStepper";
import './EnrollmentPackageViewer.css';
import {useTranslation} from "react-i18next";
import SigningConfigurationTable from "./SigningConfigurationTable";
import EnrolmentValidationErrors from "../EnrolmentValidationErrors/EnrolmentValidationErrors";
import GroupQuoteSummary from "../Quotes/GroupQuoteSummary";
import SelfInitiatedQuoteSummary from "../Quotes/SelfInitiatedQuoteSummary";
import AchieversQuoteSummary from "../Quotes/AchieversQuoteSummary";
import {useAuthenticationStatus} from "../Authentication/AuthenticationStatusProvider";
import {Link} from "react-router-dom";
import {Tooltip} from "react-tooltip";
import * as pdfjsLib from "pdfjs-dist";
import {isLanguageEnglish} from "./PDFGenerationUtils";

import Prospectus_En from "./Forms/Prospectus/CET-Prospectus-EN.pdf";
import Prospectus_Fr from "./Forms/Prospectus/CET-Prospectus-FR.pdf";

const EnrollmentPackageViewer = ({state}) => {
    const {t, i18n} = useTranslation();
    const [quoteImage, setQuoteImage] = useState('');
    const [quoteImage2, setQuoteImage2] = useState('');
    const [blobURL, setBlobURL] = useState('');
    const [envelopeId, setEnvelopeId] = useState(null); //This is the ID of the envelope that we will use to track the status of the signing process
    const [signingSetupPhase, setSigningSetupPhase] = useState(false); //This is the phase of the signing process that we are in
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [prospectusToInject, setProspectusToInject] = useState(isLanguageEnglish(i18n) ? Prospectus_En : Prospectus_Fr);
    const {authenticationStatus} = useAuthenticationStatus();

    console.log("formlang", i18n.language);

    //Set the prospectus to inject based on the language
    useEffect(() => {
        console.log("Setting prospectus to inject");
        setProspectusToInject(isLanguageEnglish(i18n) ? Prospectus_En : Prospectus_Fr);
    }, [i18n.language]);

    //When we have decided which prospectus to inject, count the number of pages and set the state
    useEffect(() => {
        if (!prospectusToInject) {
            return;
        }
        const loadingTask = pdfjsLib.getDocument(prospectusToInject); // Replace with your PDF path

        console.log(`loadingTask: ${loadingTask}`);
        loadingTask.promise.then(
            (pdf) => {
                console.log(`Prospectus loaded with ${pdf.numPages} pages`);
                state.setApplication({
                    ...state.application,
                    prospectusPages: pdf.numPages
                });
            },
            (error) => {
                console.error('Error loading PDF:', error);
            }
        );
    }, [prospectusToInject]);

    //Once the image from the quote is provided, we can generate the full PDF
    useEffect(() => {
        if (!quoteImage) { return; }
        if (state.plan2.planTypeID >= 0 && state.plan2.planTypeID != PLAN_TYPE_IDS.TRANSFER && !quoteImage2) { return; }
        //Only proceed when we have both quotes, if needed

        if (!state.docusign.envelopeId) {
            const pdfData = generateEnrolmentDocumentPDFToBlob(state, quoteImage, quoteImage2, authenticationStatus, i18n);
            setBlobURL(URL.createObjectURL(pdfData));
        }
    }, [quoteImage, quoteImage2]);

    //If we have an envelopeId, we can load the current PDF from DocuSign using the backend API
    useEffect(() => {
        if (state.docusign.envelopeId) {
            const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
            setBlobURL(`${baseUrl}/signatures/pdf/${state.enrollment._id}`);
        }
    }, [state.docusign.envelopeId]);

    /*
    //Loading the DocuSign JS library...
    //TODO: This isn't working properly... Docusign has an open issue on this and will begin work on it in August 2024
    useEffect(() => {
        // Create a script element
        const script = document.createElement('script');

        // Set the script attributes
        script.src = 'https://js-d.docusign.com/bundle.js'; // Replace with your remote script URL
        //NOTE: use src="https://js.docusign.com/bundle.js" for production
        script.async = true;

        // Append the script to the document head or body
        document.body.appendChild(script);

        // Cleanup function to remove the script when the component unmounts
        return () => {
            document.body.removeChild(script);
        };
    }, []); // Empty dependency array ensures this runs only once when the component mounts
    */

    return (
        <div className={'enrollment-package-viewer'}>
            <EnrollmentProgressStepper state={state}/>
            {quoteImage && !state.docusign.envelopeId && !signingSetupPhase && enrolmentValidation(state).length === 0 && (!state.application.masterApplicationId || (state.application.masterApplicationId && state.application.masterApplication.state !== 'pre-docusign')) &&
                <div className={'signing-table-footer'}>
                    <button
                        className={'ready-to-sign-button'}
                        onClick={() => {
                            setSigningSetupPhase(true)
                        }}>Ready to Sign
                    </button>
                </div>
            }
            {state.application.masterApplicationId && state.application.masterApplication.state === 'pre-docusign' &&
                <div className={'signing-table-footer'}>
                    <div className={'info'}>
                    <span className="material-symbols-outlined">
                        diversity_3
                    </span>
                        {t('This application is part of a Family Enrolment.  You must submit the primary enrolment for signing before you can submit this one.')}

                        <Link
                            to={`/enrolment/${state.application.masterApplicationId}/Basic`}
                        >
                            <span class="material-symbols-outlined"
                                  data-tooltip-content={`Jump to Primary Enrolment`}
                                  data-tooltip-id={'tooltip'}
                                  data-tooltip-place='top'
                            >
                                arrow_circle_right
                            </span>
                        </Link>
                    </div>
                </div>
            }
            {blobURL && (state.docusign.envelopeId || signingSetupPhase) &&
                <SigningConfigurationTable
                    state={state}
                    signingSetupPhase={signingSetupPhase}
                    setSigningSetupPhase={setSigningSetupPhase}
                    setEnvelopeId={setEnvelopeId}
                    quoteImage={quoteImage}
                    quoteImage2={quoteImage2}
                />
            }
            {blobURL &&
                //Using this condition just to make the experience a bit better
                // I.e. so that the messages and links only show up once they are clickable
                // Maybe this could be done better
                <EnrolmentValidationErrors
                    state={state}
                />
            }

            <div>
                <div className={'docusign-agreement'} id={'docusign-agreement'}></div>
            </div>

            {/*state.docusign.envelopeId &&
                <div className={'info'}>
                    Below is an up-to-date view of the current document from Docusign.
                </div>
                */
            }
            {!iframeLoaded &&
                <div className={'loading-screen'}>
                    <div>
                        <div>
                            {state.docusign.envelopeId &&
                                t('Fetching current document from DocuSign...')
                            }
                            {!state.docusign.envelopeId &&
                                t('Generating PDF...')
                            }
                        </div>
                        <div>
                            <img src={'/images/pencil.png'} alt={''} className={'wiggling'}/>
                        </div>
                    </div>
                </div>
            }
            {(!signingSetupPhase || state.docusign.envelopeId) &&

                <iframe
                    title={'Enrolment Review'}
                    src={blobURL}
                    style={{width: "100%", height: "95vh", display: iframeLoaded ? 'block' : 'none'}}
                    onLoad={() => {
                        setIframeLoaded(!!blobURL);
                    }}
                />
            }

            <div style={{position: 'absolute', left: '-1000px'}}>
                {!quoteImage && !state.docusign.envelopeId && state.plan.planTypeID === PLAN_TYPE_IDS.GROUP &&
                    <GroupQuoteSummary state={state} setQuoteImage={setQuoteImage} page={1} totalPages={2}/>
                }
                {!quoteImage && !state.docusign.envelopeId && state.plan.planTypeID === PLAN_TYPE_IDS.ACHIEVERS &&
                    <AchieversQuoteSummary state={state} setQuoteImage={setQuoteImage}/>
                }
                {!quoteImage && !state.docusign.envelopeId && state.plan.planTypeID === PLAN_TYPE_IDS.SI &&
                    <SelfInitiatedQuoteSummary state={state} setQuoteImage={setQuoteImage} page={1} totalPages={2}/>
                }
            </div>

            {state.plan2.planTypeID >= 0 &&
                <div style={{position: 'absolute', left: '-1000px'}}>
                    {!quoteImage2 && !state.docusign.envelopeId && state.plan2.planTypeID === PLAN_TYPE_IDS.GROUP &&
                        <GroupQuoteSummary state={stateWithPlan2AsPrimary(state)} setQuoteImage={setQuoteImage2} page={1} totalPages={2}/>
                    }
                    {!quoteImage2 && !state.docusign.envelopeId && state.plan2.planTypeID === PLAN_TYPE_IDS.ACHIEVERS &&
                        <AchieversQuoteSummary state={stateWithPlan2AsPrimary(state)} setQuoteImage={setQuoteImage2}/>
                    }
                    {!quoteImage2 && !state.docusign.envelopeId && state.plan2.planTypeID === PLAN_TYPE_IDS.SI &&
                        <SelfInitiatedQuoteSummary state={stateWithPlan2AsPrimary(state)} setQuoteImage={setQuoteImage2} page={1} totalPages={2}/>
                    }
                </div>
            }

            <Tooltip id={'tooltip'} />
        </div>
    )
}

export default EnrollmentPackageViewer;